import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@components';
import type { ScenarioConfig, ScenarioTestRequest, ScenarioTestResult } from './types';
import type { ApiOutcome } from '../../hooks/useScenario';
import { isSystemTool } from './scenarioDraft';
import { FieldLabel, MultiSelectField, SelectField, TextField, Toggle } from './ui';

interface Props {
  config: ScenarioConfig | null;
  callerNumber: string;
  /** Test time for today/now, local to the organization; empty means real time. */
  now: string;
  onRun: (request: ScenarioTestRequest) => Promise<ApiOutcome<ScenarioTestResult>>;
}

const JsonArea: React.FC<{ label: string; value: string; onChange: (value: string) => void; hint?: string }> = ({
  label,
  value,
  onChange,
  hint,
}) => (
  <div>
    <FieldLabel label={label} hint={hint} />
    <textarea
      aria-label={label}
      spellCheck={false}
      className="block h-28 w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-xs bg-white dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
      value={value}
      onChange={e => onChange(e.target.value)}
    />
  </div>
);

const parseObject = (text: string, label: string): Record<string, unknown> => {
  if (!text.trim()) return {};
  const parsed = JSON.parse(text);
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new Error(`${label}: JSON object expected`);
  }
  return parsed as Record<string, unknown>;
};

const Pre: React.FC<{ value: unknown }> = ({ value }) => (
  <pre className="max-h-64 overflow-auto rounded bg-gray-100 p-2 text-xs dark:bg-gray-800 dark:text-gray-100">
    {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
  </pre>
);

export const ToolTestPanel: React.FC<Props> = ({ config, callerNumber, now, onRun }) => {
  const { t } = useTranslation();
  const [toolId, setToolId] = useState('');
  const [inputs, setInputs] = useState('{}');
  const [facts, setFacts] = useState('{}');
  const [succeeded, setSucceeded] = useState<string[]>([]);
  const [useMock, setUseMock] = useState(true);
  const [mockStatus, setMockStatus] = useState('200');
  const [mockBody, setMockBody] = useState('{}');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ScenarioTestResult | null>(null);

  if (!config) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">{t('agents.scenario.testNeedsValidJson')}</p>;
  }

  const tool = config.tools.find(item => item.id === toolId);
  const isSystem = Boolean(tool && isSystemTool(tool));
  const mockActive = useMock && !isSystem;
  const toolOptions = config.tools.map(item => ({ value: item.id, label: item.title || item.id }));

  const run = async () => {
    setError(null);
    setResult(null);
    let request: ScenarioTestRequest;
    try {
      request = {
        config,
        tool_id: toolId,
        inputs: parseObject(inputs, t('agents.scenario.testInputs')),
        facts: parseObject(facts, t('agents.scenario.testFacts')),
        succeeded_tools: succeeded,
        caller_number: callerNumber || undefined,
        now: now || undefined,
        mock_response: mockActive
          ? { status: Number.parseInt(mockStatus, 10) || 200, body: mockBody.trim() ? JSON.parse(mockBody) : null }
          : null,
      };
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return;
    }
    setRunning(true);
    const outcome = await onRun(request);
    setRunning(false);
    if (outcome.ok) {
      setResult(outcome.data);
    } else {
      setError(
        outcome.errors.length
          ? outcome.errors.map(issue => `${issue.pointer}: ${issue.message}`).join('\n')
          : outcome.message
      );
    }
  };

  return (
    <div className="space-y-3" data-testid="scenario-tool-test">
      <p className="text-xs text-gray-500 dark:text-gray-400">{t('agents.scenario.testHint')}</p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <SelectField
          label={t('agents.scenario.tool')}
          value={toolId}
          options={toolOptions}
          onChange={setToolId}
          issues={[]}
          pointer="/test/tool"
          placeholder={t('agents.scenario.selectPlaceholder')}
        />
        <MultiSelectField
          label={t('agents.scenario.testSucceededTools')}
          hint={t('agents.scenario.testSucceededToolsHint')}
          values={succeeded}
          options={toolOptions.filter(option => option.value !== toolId)}
          onChange={setSucceeded}
          issues={[]}
          pointer="/test/succeeded"
        />
        <JsonArea label={t('agents.scenario.testInputs')} value={inputs} onChange={setInputs} />
        <JsonArea
          label={t('agents.scenario.testFacts')}
          hint={t('agents.scenario.testFactsHint')}
          value={facts}
          onChange={setFacts}
        />
      </div>
      {isSystem ? (
        <p className="text-xs text-warning-700 dark:text-warning-300">{t('agents.scenario.systemTools.testLiveHint')}</p>
      ) : (
        <Toggle label={t('agents.scenario.testUseMock')} checked={useMock} onChange={setUseMock} />
      )}
      {mockActive ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[140px_1fr]">
          <TextField
            label={t('agents.scenario.testMockStatus')}
            type="number"
            value={mockStatus}
            onChange={setMockStatus}
            issues={[]}
            pointer="/test/status"
          />
          <JsonArea label={t('agents.scenario.testMockBody')} value={mockBody} onChange={setMockBody} />
        </div>
      ) : isSystem ? null : (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          {tool && !isSystemTool(tool) && tool.operation === 'write'
            ? t('agents.scenario.testLiveWriteForbidden')
            : t('agents.scenario.testLiveHint')}
        </p>
      )}
      <Button size="sm" onClick={run} disabled={!toolId || running}>
        <span className="material-icons text-base mr-1">play_arrow</span>
        {running ? t('agents.scenario.testRunning') : t('agents.scenario.testRun')}
      </Button>
      {error && <pre className="whitespace-pre-wrap text-xs text-red-600 dark:text-red-400">{error}</pre>}
      {result && (
        <div className="space-y-2">
          {result.steps.map((step, index) => (
            <div key={index}>
              <FieldLabel label={`${t('agents.scenario.testStep')} ${index + 1}: ${step.function}`} />
              <Pre value={step.result} />
            </div>
          ))}
          {result.request && (
            <div>
              <FieldLabel label={t('agents.scenario.testRequest')} />
              <Pre value={result.request} />
            </div>
          )}
          {result.response && (
            <div>
              <FieldLabel label={t('agents.scenario.testResponse')} />
              <Pre value={result.response} />
            </div>
          )}
          <div>
            <FieldLabel label={t('agents.scenario.testFactsAfter')} />
            <Pre value={result.facts} />
          </div>
        </div>
      )}
    </div>
  );
};
